import { useRef, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence, animate, useMotionValue, useTransform } from "framer-motion";
import { Play, Pause, RotateCcw, Volume2, VolumeX, ChevronDown, ShieldCheck, Captions, FileText } from "lucide-react";
import { PRESENTER } from "@/lib/assets";

const SCENES = [
  { src: "/video/seg1.mp3", type: "presenter", chip: "Meet Taylor", caption: "Hi — I'm Taylor", transcript: "Hi there! I'm Taylor, and I want to tell you about an exciting new tool that was built with you in mind." },
  { src: "/video/seg2.mp3", type: "presenter", chip: "Why it matters", caption: "Your GPA opens doors — internships, scholarships, jobs", transcript: "As a student, you know how much your GPA matters when you're applying for internships, scholarships, or that perfect job." },
  { src: "/video/seg3.mp3", type: "presenter", chip: "Big question", caption: "But does your GPA tell your whole story?", transcript: "But... does your GPA really tell your whole story?" },
  { src: "/video/seg4.mp3", type: "skills", chip: "Beyond grades", caption: "Communicate? Solve problems? Adapt?", transcript: "Does it show how well you communicate? Solve problems? Work with others? Adapt when things don't go as planned?" },
  { src: "/video/seg5.mp3", type: "skills", chip: "Transferable skills", caption: "Transferable skills follow you into the workplace", transcript: "Research tells us employers value these abilities tremendously. We call them transferable skills, because they transfer from the classroom, work, and life directly into the workplace." },
  { src: "/video/seg6.mp3", type: "presenter", chip: "Introducing", caption: "Meet GPA+ and the Transferable Skills Benchmark (TSB)", transcript: "So Lone Star College created GPA+ and the Transferable Skills Benchmark, or TSB!" },
  { src: "/video/seg7.mp3", type: "demoA", chip: "How it works", caption: "Identify, develop & demonstrate your skills", transcript: "GPA+ helps you identify, develop, and demonstrate these important skills throughout your college experience." },
  { src: "/video/seg8.mp3", type: "demoB", chip: "Your TSB", caption: "Show what you can do — beyond the transcript", transcript: "And your TSB gives you a whole new way to show what you can do, beyond what appears on a traditional transcript." },
  { src: "/video/seg9.mp3", type: "presenter", chip: "Future-ready", caption: "Human skills matter as AI changes work", transcript: "And that matters in a rapidly changing workplace. As technology and AI transform how work gets done, human skills like communication, collaboration, adaptability, and critical thinking are more important than ever." },
  { src: "/video/seg10.mp3", type: "employer", chip: "For employers", caption: "A clearer picture of a candidate's potential", transcript: "For employers, GPA+ paints a clearer picture of the abilities and potential a candidate can bring to their organization." },
  { src: "/video/seg11.mp3", type: "presenter", chip: "Your story", caption: "Your GPA tells part of your story. GPA+ tells the rest.", transcript: "Your GPA tells part of your story. GPA+ helps tell the rest." },
  { src: "/video/seg12.mp3", type: "presenter", chip: "Welcome", caption: "Welcome to GPA+", transcript: "Welcome to GPA+!" },
];

const SKILL_CHIPS = ["Communication", "Problem-Solving", "Teamwork", "Adaptability", "Critical Thinking", "Leadership"];

function CountUp({ to }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  useEffect(() => {
    const controls = animate(mv, to, { duration: 0.7, ease: "easeOut" });
    return () => controls.stop();
  }, [to, mv]);
  return <motion.span>{rounded}</motion.span>;
}

function Cursor({ path }) {
  return (
    <motion.div
      className="pointer-events-none absolute z-20 drop-shadow-md"
      initial={{ left: path.left[0], top: path.top[0] }}
      animate={{ left: path.left, top: path.top }}
      transition={{ duration: path.duration, times: path.times, ease: "easeInOut" }}
    >
      <motion.div animate={{ scale: path.scale }} transition={{ duration: path.duration, times: path.times }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M4 2 L4 20 L9 15 L12.5 22 L15.5 20.8 L12 14 L19 14 Z" fill="#0f172a" stroke="#ffffff" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      </motion.div>
    </motion.div>
  );
}

function DemoFrame({ url, children }) {
  return (
    <div className="absolute inset-0 flex flex-col bg-slate-100">
      <div className="flex items-center gap-1.5 border-b border-slate-200 bg-white px-3 py-2">
        <span className="h-2 w-2 rounded-full bg-red-400" />
        <span className="h-2 w-2 rounded-full bg-amber-400" />
        <span className="h-2 w-2 rounded-full bg-emerald-400" />
        <span className="ml-3 truncate rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400">{url}</span>
      </div>
      <div className="relative flex-1 overflow-hidden p-4 sm:p-5">{children}</div>
    </div>
  );
}

function Bar({ label, value, color = "#005291", delay = 0 }) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-[11px] font-semibold text-slate-600">
        <span>{label}</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: "0%" }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.8, delay, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

// Screen-recording style demo. mode 'A' = benchmark fills in; 'B' = what-if re-weight.
function ScreenDemo({ mode }) {
  const [selected, setSelected] = useState(false);
  useEffect(() => {
    if (mode !== "B") return;
    const t = setTimeout(() => setSelected(true), 2600);
    return () => clearTimeout(t);
  }, [mode]);

  if (mode === "A") {
    return (
      <DemoFrame url="gpaplus.lonestar.edu/my-skills">
        <div className="flex items-center gap-5">
          <div className="text-center">
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-[6px] border-[#78be21]/25">
              <div className="absolute inset-0 rounded-full border-[6px] border-transparent border-t-[#78be21] border-r-[#78be21]" style={{ transform: "rotate(45deg)" }} />
              <span className="font-mono-num text-2xl font-bold text-[#003057]"><CountUp to={82} /></span>
            </div>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-[#5fa114]">TSB / 100</p>
          </div>
          <div className="flex-1 space-y-2.5">
            <Bar label="Communication" value={78} delay={0.1} />
            <Bar label="Critical Thinking" value={85} color="#78be21" delay={0.25} />
            <Bar label="Collaboration" value={74} delay={0.4} />
            <Bar label="Resilience" value={69} color="#78be21" delay={0.55} />
          </div>
        </div>
        <Cursor path={{ left: ["12%", "60%", "60%"], top: ["78%", "40%", "40%"], scale: [1, 1, 1], duration: 3.5, times: [0, 0.7, 1] }} />
      </DemoFrame>
    );
  }

  // mode B
  const bars = selected
    ? [["Communication", 84], ["Problem-Solving", 88], ["Teamwork", 72], ["Adaptability", 79]]
    : [["Communication", 62], ["Problem-Solving", 70], ["Teamwork", 58], ["Adaptability", 55]];
  return (
    <DemoFrame url="gpaplus.lonestar.edu/employment-profile">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#5fa114]">Target career</p>
          <motion.div
            key={selected ? "sel" : "unsel"}
            initial={{ scale: 0.94, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`mt-1 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-bold ${
              selected ? "border-[#78be21] bg-[#78be21]/10 text-[#003057]" : "border-slate-300 bg-white text-slate-500"
            }`}
          >
            {selected ? "UX Design" : "Undecided"} <ChevronDown className="h-3.5 w-3.5" />
          </motion.div>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fit</p>
          <p className="font-mono-num text-2xl font-bold text-[#78be21]">
            <CountUp to={selected ? 88 : 71} />%
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        {bars.map(([label, value], i) => (
          <Bar key={label} label={label} value={value} color={i % 2 ? "#78be21" : "#005291"} delay={selected ? i * 0.12 : 0} />
        ))}
      </div>
      <Cursor
        path={{
          left: ["80%", "22%", "22%", "22%", "40%"],
          top: ["85%", "12%", "12%", "12%", "60%"],
          scale: [1, 1, 0.8, 1, 1],
          duration: 5.5,
          times: [0, 0.42, 0.48, 0.54, 1],
        }}
      />
    </DemoFrame>
  );
}

export function OverviewVideo() {
  const audioRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  const playIndex = useCallback((i) => {
    const a = audioRef.current;
    if (!a) return;
    a.src = SCENES[i].src;
    a.play().then(() => { setPlaying(true); setStarted(true); setEnded(false); }).catch(() => {});
  }, []);

  const start = () => { setIndex(0); playIndex(0); };

  const toggle = () => {
    const a = audioRef.current;
    if (!started || ended) { start(); return; }
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const onEnded = () => {
    if (index < SCENES.length - 1) { const n = index + 1; setIndex(n); playIndex(n); }
    else { setPlaying(false); setEnded(true); setProgress(0); }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (a && a.duration) setProgress(a.currentTime / a.duration);
  };

  const replay = () => { setIndex(0); playIndex(0); };
  const toggleMute = () => { const a = audioRef.current; if (a) { a.muted = !a.muted; setMuted(a.muted); } };
  const jumpTo = (i) => { setIndex(i); playIndex(i); };

  const [captionsOn, setCaptionsOn] = useState(true);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const scene = SCENES[index];
  const isDemo = scene.type === "demoA" || scene.type === "demoB";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7 }}
      className="relative"
      data-testid="overview-video"
    >
      <div className="group relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
        <div className="relative aspect-[3/2] w-full overflow-hidden bg-[#00243f]">
          {/* MEDIA */}
          <AnimatePresence mode="wait">
            {isDemo ? (
              <motion.div key={`demo-${index}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0">
                <ScreenDemo mode={scene.type === "demoA" ? "A" : "B"} />
              </motion.div>
            ) : (
              <motion.div key={`pres-${index}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0">
                <motion.img
                  src={PRESENTER}
                  alt="GPA+ student presenter at Lone Star College–Tomball"
                  className="h-full w-full object-cover"
                  animate={playing ? { scale: [1, 1.05, 1.03, 1.06, 1], y: [0, -6, -3, -7, 0] } : { scale: 1, y: 0 }}
                  transition={playing ? { duration: 9, ease: "easeInOut", repeat: Infinity } : { duration: 0.6 }}
                />
                {scene.type === "presenter" && playing && (
                  <motion.div
                    className="pointer-events-none absolute inset-0"
                    animate={{ opacity: [0.0, 0.12, 0.0] }}
                    transition={{ duration: 3.2, ease: "easeInOut", repeat: Infinity }}
                    style={{ background: "radial-gradient(400px 260px at 50% 42%, rgba(120,190,33,0.35), transparent 70%)" }}
                  />
                )}
                {scene.type === "skills" && (
                  <div className="absolute inset-x-0 bottom-6 flex flex-wrap items-end justify-center gap-2 px-8">
                    {SKILL_CHIPS.map((s, i) => (
                      <motion.span key={s} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.12 }}
                        className="rounded-full bg-white/90 px-3 py-1.5 text-sm font-bold text-[#003057] shadow-lg backdrop-blur">
                        {s}
                      </motion.span>
                    ))}
                  </div>
                )}
                {scene.type === "employer" && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
                    className="absolute right-4 top-1/2 hidden w-56 -translate-y-1/2 rounded-xl bg-white/95 p-3 shadow-xl backdrop-blur sm:block">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Best-fit candidate</p>
                    <p className="mt-1 font-bold text-[#003057]">Grace O. · 92% fit</p>
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#005291]/10 px-2 py-0.5 text-[10px] font-bold text-[#005291]">
                      <ShieldCheck className="h-3 w-3" /> 360 Verified
                    </span>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {!isDemo && <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#00243f]/85 via-transparent to-[#00243f]/20" />}

          {/* scene chip */}
          {started && (
            <div className="absolute left-4 top-4 z-10">
              <span className="rounded-full bg-[#78be21] px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#00243f] shadow" data-testid="video-chip">
                {scene.chip}
              </span>
            </div>
          )}

          {/* idle overlay */}
          {(!started || (!playing && !ended)) && (
            <button onClick={toggle} data-testid="video-play-overlay"
              className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-[#00243f]/35 backdrop-blur-[1px] transition-colors hover:bg-[#00243f]/45">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#78be21] text-[#00243f] shadow-xl transition-transform group-hover:scale-105">
                <Play className="ml-1 h-7 w-7 fill-current" />
              </span>
              {!started && <span className="rounded-full bg-black/40 px-4 py-1.5 text-sm font-bold text-white">Watch the 90-second overview</span>}
            </button>
          )}

          {/* replay overlay */}
          {ended && (
            <button onClick={replay} data-testid="video-replay-overlay"
              className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-[#00243f]/45">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#003057] shadow-xl"><RotateCcw className="h-7 w-7" /></span>
              <span className="rounded-full bg-black/40 px-4 py-1.5 text-sm font-bold text-white">Replay</span>
            </button>
          )}
        </div>

        {/* control bar — hidden until hover */}
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 bg-gradient-to-t from-[#00243f] to-transparent px-4 pb-3 pt-8 opacity-0 transition-opacity duration-300 group-hover:opacity-100 focus-within:opacity-100">
          <button onClick={toggle} data-testid="video-toggle" aria-label={playing ? "Pause" : "Play"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4 fill-current" />}
          </button>
          <div className="flex flex-1 items-center gap-1.5" data-testid="video-progress">
            {SCENES.map((_, i) => (
              <button key={i} onClick={() => jumpTo(i)} aria-label={`Scene ${i + 1}`} className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/25">
                <span className="block h-full rounded-full bg-[#78be21] transition-[width] duration-150"
                  style={{ width: i < index ? "100%" : i === index && started ? `${Math.round(progress * 100)}%` : "0%" }} />
              </button>
            ))}
          </div>
          <button onClick={() => setCaptionsOn((c) => !c)} data-testid="video-captions" aria-label={captionsOn ? "Hide captions" : "Show captions"} title="Captions"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full backdrop-blur transition-colors ${captionsOn ? "bg-[#78be21] text-[#00243f]" : "bg-white/15 text-white hover:bg-white/25"}`}>
            <Captions className="h-4 w-4" />
          </button>
          <button onClick={toggleMute} data-testid="video-mute" aria-label={muted ? "Unmute" : "Mute"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* floating TSB stat (hidden during screen-demo scenes) */}
      {!isDemo && (
        <div className="glass absolute -top-5 -right-4 hidden rounded-2xl p-4 text-white shadow-xl sm:block">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#a8e063]">Overall TSB</p>
          <p className="font-mono-num text-3xl font-bold">3.62</p>
          <p className="text-xs text-slate-300">Advanced · 87/100</p>
        </div>
      )}

      {/* live caption — below the video, never over it (toggle with CC) */}
      {started && captionsOn && (
        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm" data-testid="video-caption-panel">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#a8e063]">Now playing · {scene.chip}</p>
          <AnimatePresence mode="wait">
            <motion.p key={index} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35 }}
              className="mt-1 font-heading text-base font-bold leading-snug text-white sm:text-lg" data-testid="video-caption">
              {scene.transcript}
            </motion.p>
          </AnimatePresence>
        </div>
      )}

      {/* full transcript (accessibility) */}
      <div className="mt-3">
        <button onClick={() => setTranscriptOpen((o) => !o)} data-testid="video-transcript-toggle"
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-[#a8e063]">
          <FileText className="h-3.5 w-3.5" /> {transcriptOpen ? "Hide transcript" : "Read full transcript"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${transcriptOpen ? "rotate-180" : ""}`} />
        </button>
        {transcriptOpen && (
          <div className="mt-3 space-y-2 rounded-xl border border-white/10 bg-white/5 p-4 text-sm leading-relaxed" data-testid="video-transcript">
            {SCENES.map((s, i) => (
              <p key={i} onClick={() => jumpTo(i)} className={`cursor-pointer transition-colors ${i === index && started ? "text-white font-semibold" : "text-slate-400 hover:text-slate-200"}`}>
                {s.transcript}
              </p>
            ))}
          </div>
        )}
      </div>

      <audio ref={audioRef} onEnded={onEnded} onTimeUpdate={onTime} preload="auto" />
    </motion.div>
  );
}
